import { useEffect, useMemo, useState } from "react";
import { Chart as ChartJS, BubbleController, PointElement, LinearScale, LogarithmicScale, Tooltip, TooltipItem } from "chart.js";
import { Bubble } from "react-chartjs-2";
import { Errors, FirmwareErrorStat } from "@app/shared";
import { Icons } from "../../shared";
import { HoverTooltip } from "../../shared/HoverTooltip";
import { compareVersionsDesc } from "../../../helpers";

ChartJS.register(BubbleController, PointElement, LinearScale, LogarithmicScale, Tooltip);

// Highlight-and-context colouring. The few builds that matter — the newest ones — get vivid,
// hue-separated colours (validated worst-adjacent CVD ΔE 24.9), so they pop off the chart AND
// stay unambiguous in the small legend swatches. Older builds sit in a single-hue green ramp
// keyed by release train (newest train darkest), so the eye goes straight to the highlights.
const RECENT_COLORS = [
    "#2a78d6", // blue
    "#eb6834", // orange
    "#e87ba4", // magenta
];
// Single-hue green ordinal ramp for the older release trains, newest train darkest. Stepped by
// lightness (validated: monotone, visible gaps, light end clears 2:1) so adjacent legend dots
// stay distinguishable and never collide with the vivid highlight hues.
const TRAIN_GREENS = [
    "#064a20",
    "#0f7a38",
    "#3ba05a",
    "#63bd7d",
];
const OTHER_COLOR = "#63bd7d"; // trains beyond the ramp fold into the lightest green

// Release train = major.minor (the first two dotted parts). Versions arrive already
// filtered to well-formed four-part builds by the API, so this always has two parts.
const releaseTrain = (version: string): string => version.split(".").slice(0, 2).join(".");

// The N newest builds get their own colour (rather than blending into their train's blob),
// so a fresh rollout that starts regressing stands out immediately.
const RECENT_HIGHLIGHT_COUNT = 3;

// Stale-while-revalidate, mirroring the trend card: repaint the last result instantly on
// remount, then swap in fresh data. Safe because the server also caches this briefly.
let scatterCache: { stats: FirmwareErrorStat[]; at: number } | undefined;
const SCATTER_CACHE_TTL_MS = 60_000;

/**
 * Fleet-wide bubble chart making problematic firmware builds obvious at a glance.
 * X = total error volume, Y = number of distinct error types, bubble size = devices that
 * reported an error on that build. Colour: the 3 newest builds each get their own vivid hue so a
 * fresh rollout stands out; older builds sit in a green ramp by release train (newest darkest).
 * A build sitting to the upper-right — lots of volume AND lots of distinct failures — is the
 * one to look at; a large bubble there means it's hitting many devices.
 */
export const DeviceErrorFirmwareScatterCard: React.FC = () => {
    const [stats, setStats] = useState<FirmwareErrorStat[]>(scatterCache?.stats ?? []);
    const [loaded, setLoaded] = useState(!!scatterCache);

    useEffect(() => {
        let cancelled = false;
        if (scatterCache && Date.now() - scatterCache.at < SCATTER_CACHE_TTL_MS) {
            setStats(scatterCache.stats);
            setLoaded(true);
        }
        (async () => {
            const result = (await Errors.getFirmwareErrorScatter(true)) ?? [];
            scatterCache = { stats: result, at: Date.now() };
            if (!cancelled) {
                setStats(result);
                setLoaded(true);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    // The N newest full versions, singled out from their trains for their own colour.
    const recentVersions = useMemo(() => {
        // API returns newest-first, but sort defensively so this holds regardless.
        return [...stats]
            .sort((a, b) => compareVersionsDesc(a.firmwareVersion, b.firmwareVersion))
            .slice(0, RECENT_HIGHLIGHT_COUNT)
            .map((s) => s.firmwareVersion ?? "")
            .filter(Boolean);
    }, [stats]);

    // A build's colour group: its full version if it's one of the highlighted recent builds,
    // otherwise its release train (major.minor).
    const groupKeyOf = useMemo(
        () => (version: string) => (recentVersions.includes(version) ? version : releaseTrain(version)),
        [recentVersions]
    );

    // Group → colour mapping. Highlighted recent versions get the vivid hues (newest first);
    // older trains get the green ramp, newest train darkest. Colour follows the group.
    const colorMap = useMemo(() => {
        const map = new Map<string, string>();
        recentVersions.forEach((v, i) => map.set(v, RECENT_COLORS[i] ?? RECENT_COLORS[RECENT_COLORS.length - 1]));
        const trains: string[] = [];
        for (const s of stats) {
            const v = s.firmwareVersion ?? "";
            if (recentVersions.includes(v)) continue;
            const t = releaseTrain(v);
            if (t && !trains.includes(t)) trains.push(t);
        }
        trains.forEach((t, i) => map.set(t, i < TRAIN_GREENS.length ? TRAIN_GREENS[i] : OTHER_COLOR));
        return map;
    }, [stats, recentVersions]);

    // Bubble radius encodes device count by AREA (sqrt of the value), so a build on 4x the
    // devices reads as 4x the area rather than 4x the radius. Clamped to a legible band.
    const maxDevices = useMemo(() => Math.max(1, ...stats.map((s) => s.deviceCount)), [stats]);
    const radiusFor = (deviceCount: number) => {
        const MIN_R = 5, MAX_R = 26;
        return MIN_R + (MAX_R - MIN_R) * Math.sqrt(Math.max(0, deviceCount) / maxDevices);
    };

    // One dataset per colour group (a highlighted recent version, or a release train).
    const datasets = useMemo(() => {
        const byGroup = new Map<string, FirmwareErrorStat[]>();
        for (const s of stats) {
            const key = groupKeyOf(s.firmwareVersion ?? "");
            (byGroup.get(key) ?? byGroup.set(key, []).get(key)!).push(s);
        }
        return [...byGroup.entries()].map(([key, items]) => ({
            label: key,
            data: items.map((s) => ({
                x: s.totalErrorCount,
                y: s.uniqueErrorTypes,
                r: radiusFor(s.deviceCount),
                version: s.firmwareVersion,
                devices: s.deviceCount,
            })),
            backgroundColor: `${colorMap.get(key) ?? OTHER_COLOR}b3`, // ~70% alpha fill
            borderColor: colorMap.get(key) ?? OTHER_COLOR,
            borderWidth: 1.5,
            hoverBorderWidth: 2,
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stats, colorMap, groupKeyOf, maxDevices]);

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: {
                callbacks: {
                    title: (items: TooltipItem<"bubble">[]) => `v${(items[0]?.raw as { version?: string })?.version ?? ""}`,
                    label: (item: TooltipItem<"bubble">) => {
                        const { x, y, devices } = item.raw as { x: number; y: number; devices: number };
                        return [
                            `${x.toLocaleString()} total errors`,
                            `${y.toLocaleString()} unique error type${y === 1 ? "" : "s"}`,
                            `${devices.toLocaleString()} device${devices === 1 ? "" : "s"} reporting`,
                        ];
                    },
                },
            },
        },
        scales: {
            // Log scale — error volume spans orders of magnitude across builds (and the
            // migration left some legacy buckets clamped to 200M); a linear axis would
            // squash every well-behaved build to x≈0. Every build has >=1 error, so log is safe.
            x: {
                type: "logarithmic" as const,
                title: { display: true, text: "Total error count (log)", color: "#898781", font: { size: 10 } },
                grid: { color: "#e1e0d9" },
                border: { display: false },
                ticks: { color: "#898781", font: { size: 9 } },
            },
            y: {
                type: "linear" as const,
                beginAtZero: true,
                title: { display: true, text: "Unique error types", color: "#898781", font: { size: 10 } },
                grid: { color: "#e1e0d9" },
                border: { display: false },
                ticks: { color: "#898781", font: { size: 9 }, precision: 0, maxTicksLimit: 6 },
            },
        },
        animation: false as const,
    };

    // Legend entries, recent highlighted versions first (they lead the colour map), then trains.
    const legend = [...colorMap.entries()];

    return (
        <div className="border border-separators-page p-5 flex flex-col gap-2 min-w-[24rem] flex-[2] shadow-sm">
            <div className="flex items-center justify-between gap-1">
                <p className="uppercase text-lg whitespace-nowrap">Firmware error footprint</p>
                <HoverTooltip hoverable={<span className="cursor-help"><Icons.Info className="text-brand-primary h-3.5 w-3.5" /></span>}>
                    <div className="text-left text-xs text-gray-600 space-y-2 max-w-xs">
                        <p>
                            Each bubble is a firmware build over the last 30 days.
                            <span className="font-semibold"> X</span> is its total error volume,
                            <span className="font-semibold"> Y</span> the number of distinct error types, and the
                            <span className="font-semibold"> bubble size</span> the number of devices that reported an
                            error on it. The 3 newest builds each get their own vivid colour (bold in the
                            legend) so they stand out; older builds are shaded green by release train
                            (major.minor) with the newest train darkest.
                        </p>
                        <p>
                            A build toward the <span className="font-semibold">upper-right</span> is the one to look at —
                            high volume and many distinct failures. A large bubble there means it&apos;s hitting many
                            devices, not just one noisy unit.
                        </p>
                        <p className="text-gray-400">
                            Versions reflect the build reported at error time, and ignored issues are excluded (matching
                            the table&apos;s default). Builds without a well-formed four-part version are omitted.
                        </p>
                    </div>
                </HoverTooltip>
            </div>
            <div className="flex-grow h-56 relative">
                {!loaded ? (
                    <p className="text-sm text-gray-500 italic">loading…</p>
                ) : stats.length === 0 ? (
                    <p className="text-sm text-gray-500 italic">no firmware errors in the last 30 days</p>
                ) : (
                    <Bubble data={{ datasets }} options={options} />
                )}
            </div>
            {legend.length > 0 && (
                <div className="flex items-center flex-wrap gap-x-3 gap-y-0.5 text-xs text-gray-500">
                    {legend.map(([key, color]) => {
                        const isRecent = recentVersions.includes(key);
                        return (
                            <span key={key} className="flex items-center gap-1">
                                <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} />
                                <span className={isRecent ? "font-semibold" : undefined}>v{key}</span>
                            </span>
                        );
                    })}
                </div>
            )}
        </div>
    );
};
